import React, { useState, useEffect } from 'react';
import './PwaInstallPrompt.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDownload,
    faMobileAlt,
    faBolt,
    faTimes,
    faCheckCircle,
    faShareSquare,
    faDesktop
} from '@fortawesome/free-solid-svg-icons';
import logo from '../../images/logo.png';
import useSiteConfig from '../useSiteConfig';

export default function PwaInstallPrompt() {
    const { logo: siteLogo } = useSiteConfig();
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [showInstructions, setShowInstructions] = useState(false);
    const [deviceType, setDeviceType] = useState('desktop');

    useEffect(() => {
        // Detectar tipo de dispositivo
        const ua = navigator.userAgent || '';
        if (/iphone|ipad|ipod/i.test(ua)) {
            setDeviceType('ios');
        } else if (/android/i.test(ua)) {
            setDeviceType('android');
        } else {
            setDeviceType('desktop');
        }

        // Si la app ya está corriendo en modo standalone/instalada, no mostrar
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        if (isStandalone) {
            return;
        }

        // Capturar evento nativo de instalación PWA si el navegador lo emite
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // "cada vez que ingresas a la pagina": mostrar con animación después de 1 segundo
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 1100);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            clearTimeout(timer);
        };
    }, []);

    const handleInstallClick = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                setIsVisible(false);
            }
            setDeferredPrompt(null);
        } else {
            // Si el navegador no disparó beforeinstallprompt (ej. Safari en iOS o Chrome sin prompt automático),
            // mostramos instrucciones claras paso a paso
            setShowInstructions(true);
        }
    };

    const handleDismiss = () => {
        setIsVisible(false);
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div className="pwaBackdrop" onClick={handleDismiss}>
            <div className="pwaModalCard" onClick={(e) => e.stopPropagation()}>
                {/* Botón cerrar */}
                <button
                    className="pwaCloseBtn"
                    onClick={handleDismiss}
                    aria-label="Cerrar aviso de instalación"
                    type="button"
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>

                {/* Cabecera */}
                <div className="pwaHeader">
                    <div className="pwaLogoWrapper">
                        <img
                            src={siteLogo || logo}
                            alt="DXN Tingo María"
                            className="pwaLogoImg"
                            onError={(e) => { e.target.src = logo; }}
                        />
                        <span className="pwaPulseBadge" />
                    </div>
                    <div className="pwaHeaderInfo">
                        <span className="pwaBadge">
                            <FontAwesomeIcon icon={faCheckCircle} /> App Oficial
                        </span>
                        <h3 className="pwaTitle">Instalar DXN Tingo María</h3>
                        <p className="pwaSubtitle">Acceso instantáneo desde tu dispositivo</p>
                    </div>
                </div>


                {/* Guía en caso de requerir instalación manual */}
                {showInstructions && (
                    <div className="pwaInstructionsBox">
                        <div className="pwaInstructionsTitle">
                            <FontAwesomeIcon icon={deviceType === 'ios' ? faShareSquare : (deviceType === 'android' ? faMobileAlt : faDesktop)} />
                            <span>Cómo instalar en tu dispositivo:</span>
                        </div>
                        {deviceType === 'ios' && (
                            <ol className="pwaInstructionsList">
                                <li>Toca el botón <strong>Compartir</strong> (icono de cuadrado con flecha hacia arriba) en Safari.</li>
                                <li>Desplázate hacia abajo y selecciona <strong>"Añadir a la pantalla de inicio"</strong>.</li>
                                <li>Confirma tocando <strong>"Añadir"</strong> en la esquina superior.</li>
                            </ol>
                        )}
                        {deviceType === 'android' && (
                            <ol className="pwaInstructionsList">
                                <li>Toca los <strong>tres puntos</strong> (⋮) en la esquina superior de Chrome.</li>
                                <li>Selecciona <strong>"Instalar aplicación"</strong> o <strong>"Añadir a la pantalla principal"</strong>.</li>
                                <li>Acepta la confirmación de instalación.</li>
                            </ol>
                        )}
                        {deviceType === 'desktop' && (
                            <ol className="pwaInstructionsList">
                                <li>Haz clic en el icono de <strong>Instalar</strong> (ordenador con flecha) ubicado en la barra de direcciones de tu navegador (esquina derecha).</li>
                                <li>O abre el menú de tu navegador y selecciona <strong>"Instalar DXN TINGO MARÍA..."</strong>.</li>
                            </ol>
                        )}
                    </div>
                )}

                {/* Acciones */}
                <div className="pwaActions">
                    <button
                        className="pwaInstallBtn"
                        onClick={handleInstallClick}
                        type="button"
                    >
                        <FontAwesomeIcon icon={faDownload} />
                        <span>{showInstructions ? "¡Entendido!" : "Instalar Aplicación"}</span>
                    </button>
                    <button
                        className="pwaDismissBtn"
                        onClick={handleDismiss}
                        type="button"
                    >
                        Más tarde
                    </button>
                </div>
            </div>
        </div>
    );
}
